import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine,
    faUsers,
    faStore,
    faSitemap,
    faMapMarkerAlt,
    faChartBar,
    faCog,
    faTableColumns,
    faEnvelope,
    faDiagramSankey,
    faSms
} from '@fortawesome/pro-regular-svg-icons';
import SettingsModal from '../../Settings/SettingsModal';
import styles from './Sidebar.module.scss';

interface SidebarProps {
    isOpen: boolean;
    onClose: () => void;
    isMobile: boolean;
    className?: string;
}

interface NavigationItem {
    id: string;
    label: string;
    path?: string;
    icon: any;
    onClick?: () => void;
    comingSoon?: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, isMobile, className }) => {
    const location = useLocation();
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

    const handleNavClick = () => {
        if (isMobile) {
            onClose();
        }
    };

    const handleSettingsClick = () => {
        setIsSettingsModalOpen(true);
        if (isMobile) {
            onClose();
        }
    };

    const handleSettingsClose = () => {
        setIsSettingsModalOpen(false);
    };

    const navigationItems: NavigationItem[] = [
        {
            id: 'dashboard',
            label: 'Dashboard',
            path: '/dashboard',
            icon: faTableColumns
        },
        {
            id: 'organizations',
            label: 'Organizations',
            path: '/organizations',
            icon: faSitemap
        },
        {
            id: 'locations',
            label: 'Locations',
            path: '/locations',
            icon: faStore
        },
        {
            id: 'email-marketing',
            label: 'Email Marketing',
            path: '/email-marketing',
            icon: faEnvelope
        },
        {
            id: 'automations',
            label: 'Automations',
            icon: faDiagramSankey,
            comingSoon: true
        },
        {
            id: 'sms-campaigns',
            label: 'SMS Campaigns',
            icon: faSms,
            comingSoon: true
        },
        /*
        {
            id: 'reports',
            label: 'Reports',
            path: '/reports',
            icon: faChartLine
        },
        */
        {
            id: 'settings',
            label: 'Settings',
            icon: faCog,
            onClick: handleSettingsClick
        }
    ];

    return (
        <>
            {/* Mobile overlay */}
            {isMobile && isOpen && (
                <div
                    className={styles.overlay}
                    onClick={onClose}
                    aria-hidden="true"
                />
            )}

            {/* Sidebar */}
            <aside
                className={`
                    ${styles.sidebar} 
                    ${isOpen ? styles.open : ''} 
                    ${isMobile ? styles.mobile : styles.desktop}
                    ${className || ''}
                `}
            >
                <nav className={styles.navigation}>
                    <ul className={styles.navList}>
                        {navigationItems.map(item => (
                            <li
                                key={item.id}
                                className={styles.navItem}
                            >
                                {item.comingSoon ? (
                                    <div
                                        className={`${styles.navLink} ${styles.disabled}`}
                                        aria-disabled="true"
                                    >
                                        <FontAwesomeIcon
                                            icon={item.icon}
                                            className={styles.navIcon}
                                        />
                                        <span className={styles.navLabel}>{item.label}</span>
                                        <span className={styles.soonTag}>Soon</span>
                                    </div>
                                ) : item.onClick ? (
                                    <button
                                        className={styles.navLink}
                                        onClick={() => {
                                            item.onClick?.();
                                            handleNavClick();
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            icon={item.icon}
                                            className={styles.navIcon}
                                        />
                                        <span className={styles.navLabel}>{item.label}</span>
                                    </button>
                                ) : (
                                    <NavLink
                                        to={item.path || '#'}
                                        className={styles.navLink}
                                        activeClassName={styles.active}
                                        onClick={handleNavClick}
                                    >
                                        <FontAwesomeIcon
                                            icon={item.icon}
                                            className={styles.navIcon}
                                        />
                                        <span className={styles.navLabel}>{item.label}</span>
                                    </NavLink>
                                )}
                            </li>
                        ))}
                    </ul>
                </nav>
            </aside>

            {/* Settings Modal */}
            {isSettingsModalOpen && (
                <SettingsModal
                    isOpen={isSettingsModalOpen}
                    onClose={handleSettingsClose}
                />
            )}
        </>
    );
};

export default Sidebar;
